import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ShopifyService from '../../services/shopify';
import { ShopifyCollection, ShopifyImage } from '../../types/shopify';
import './ArchivePage.scss';

const ArchivePage: React.FC = () => {
  const { handle } = useParams<{ handle?: string }>();
  const navigate = useNavigate();
  const [collections, setCollections] = useState<ShopifyCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<ShopifyCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    // Only fetch list when not viewing a specific collection
    if (handle) {
      setLoading(false);
      return;
    }
    
    const fetchCollections = async () => {
      setLoading(true);
      try {
        const data = await ShopifyService.getCollections();
        setCollections(data);
      } catch (error) {
        console.error('Error fetching collections:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [handle]);

  useEffect(() => {
    const fetchSelectedCollection = async () => {
      if (handle) {
        setLoading(true);
        try {
          const collection = await ShopifyService.getCollection(handle);
          setSelectedCollection(collection);
        } catch (error) {
          console.error('Error fetching collection:', error);
        }
        setLoading(false);
      } else {
        setSelectedCollection(null);
      }
    };

    fetchSelectedCollection();
  }, [handle]);

  const handleCollectionClick = (collectionHandle: string) => {
    navigate(`/archive/${collectionHandle}`);
  };

  const handleBackClick = () => {
    navigate('/archive');
  };

  // Detail view: Show stacked images from the collection
  if (handle && selectedCollection) {
    const images = selectedCollection.collectionImages || [];
    
    return (
      <div className="archive-detail">
        <button 
          className="archive-detail__back" 
          onClick={handleBackClick}
          aria-label="Back to archive"
        >
          ←
        </button>
        
        <div className="archive-detail__title-container">
          <h1 className="archive-detail__title">{selectedCollection.title}</h1>
        </div>

        {loading ? (
          <div className="archive-detail__loading">
            <div className="archive-detail__loading-bar" />
          </div>
        ) : images.length > 0 ? (
          <div className="archive-detail__images">
            {images.map((image: ShopifyImage, index: number) => (
              <div 
                key={image.id || index}
                className="archive-detail__image-wrapper"
                style={{
                  '--aspect': image.width && image.height 
                    ? image.width / image.height 
                    : 1
                } as React.CSSProperties}
              >
                <img
                  src={image.url}
                  alt={image.altText || `${selectedCollection.title} - Image ${index + 1}`}
                  className="archive-detail__image"
                  loading={index < 3 ? 'eager' : 'lazy'}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="archive-detail__empty">
            <span>No images available</span>
          </div>
        )}
      </div>
    );
  }

  // Archive overview: Show collections as thin vertical strips
  return (
    <div className="archive-page">
      {loading ? (
        <div className="archive-page__loading">
          <div className="archive-page__loading-bar" />
        </div>
      ) : collections.length > 0 ? (
        <div className="archive-page__strips">
          {collections.map((collection, index) => (
            <button
              key={collection.id}
              className={`archive-page__strip ${hoveredIndex === index ? 'archive-page__strip--active' : ''}`}
              onClick={() => handleCollectionClick(collection.handle)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="archive-page__strip-image">
                {collection.image ? (
                  <img 
                    src={collection.image.url} 
                    alt={collection.title}
                    loading="lazy"
                  />
                ) : (
                  <div className="archive-page__strip-placeholder" />
                )}
              </div>
              <div className="archive-page__strip-label">
                <span className="archive-page__strip-title">{collection.title}</span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="archive-page__empty">
          <span>No collections available</span>
        </div>
      )}
    </div>
  );
};

export default ArchivePage;
